var _excluded = ["mask", "onDateChange", "helpText", "isDateRange", "range", "minDate", "maxDate", "value", "utils"];
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import PropTypes from 'prop-types';
import MaskedFormField from './../masked-form-field';
import DateString from './../../utilities/date-string/';
var constants = {
  mask: '00/00/0000',
  placeholder: 'MM/DD/YYYY'
};
var propTypes = {
  /** Help text that displays under the formField. */
  helpText: PropTypes.node,
  /** The mask for the contained masked form field. */
  mask: PropTypes.oneOf(['0000', '00/00', '00-00', '00/0000', '00-0000', constants.mask, '00-00-0000', '0000/00/00', '0000-00-00']),
  /** onDateChange callback. Triggers everytime the value of the field changes. */
  onDateChange: PropTypes.func,
  /** @ignore An array containing two dates that the written date must be within in order to be valid. */
  range: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  /** The minimum date that can be selected. Allows date objects and valid date strings. */
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** The maximum date that can be selected. Allows date objects and valid date strings. */
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** Sets the value attribute of the date field component. Allows date objects and valid date strings. */
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** Enter a range of dates */
  isDateRange: PropTypes.bool,
  /** Add utility objects for styling */
  utils: PropTypes.object
};
var defaultProps = {
  mask: constants.mask,
  onDateChange: function onDateChange() {},
  range: null,
  minDate: null,
  maxDate: null,
  value: '',
  helpText: '',
  isDateRange: false,
  utils: {}
};
function maskToDateStringFormat(mask) {
  var stringsByMask = _defineProperty(_defineProperty(_defineProperty(_defineProperty({
    '0000': 'YYYY',
    '00/00': 'MM/DD',
    '00-00': 'MM-DD',
    '00/0000': 'MM/YYYY',
    '00-0000': 'MM-YYYY'
  }, constants.mask, constants.placeholder), '00-00-0000', 'MM-DD-YYYY'), '0000/00/00', 'YYYY/MM/DD'), '0000-00-00', 'YYYY-MM-DD');
  return stringsByMask[mask] || constants.placeholder;
}

/**
 * @ignore @description
 * ## DateField Component
 * The DateField component provides a way to select a single value from a pre-determined calendar date set.
 *
 * @ignore @see Mesh: [DateField Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/forms-datefield--default)
 *
 * @ignore @example
 * ```jsx
 * import { DateField } from 'mesh-component-library';
 *
 * <DateField
 *  id="date-field-1"
 *  label="Date"
 *  onDateChange={details => {
 *    console.log(details);
 *  }}
 * />;
 * ```
 */

var DateField = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var mask = props.mask,
    onDateChange = props.onDateChange,
    helpText = props.helpText,
    isDateRange = props.isDateRange,
    range = props.range,
    minDate = props.minDate,
    maxDate = props.maxDate,
    value = props.value,
    utils = props.utils,
    other = _objectWithoutProperties(props, _excluded);
  var useRange = range || [minDate, maxDate];
  var useMask = isDateRange ? "".concat(mask, " \u2014 ").concat(mask) : mask;
  /**
   * The date string format to format dates strings by.
   *
   * @type {String}
   * @default 'MM/DD/YYYY'
   */
  var dateStringFormat = DateField.maskToDateStringFormat(mask);

  /**
   * Mask callback. Gather the value, create a date of it
   * and pass this information along to onDateChange.
   *
   * @method dateField._onMask
   * @param {Object} details
   * @param {String} details.value - the value of the date field
   * @protected
   */
  var handleOnMask = function handleOnMask(details) {
    var date;
    if (details.value.includes('*')) {
      date = 'Cannot Parse Date';
    } else if (isDateRange) {
      date = details.value.split(" \u2014 ").map(function (d) {
        return DateString.toDate(d, dateStringFormat, useRange);
      });
    } else {
      date = DateString.toDate(details.value, dateStringFormat, useRange);
    }
    onDateChange({
      value: details.value,
      date: date
    });
  };
  var placeholder = maskToDateStringFormat(mask).toLowerCase();
  var validatedValue = DateString.fromUserFromDate(value, dateStringFormat);
  return /*#__PURE__*/React.createElement(MaskedFormField, _extends({}, other, {
    helpText: helpText || '',
    value: validatedValue,
    placeholder: placeholder,
    mask: useMask,
    onMask: handleOnMask,
    ref: forwardedRef,
    utils: utils
  }));
});

/**
 * Converts the provided mask to a date string format.
 * @param {String} mask
 * @returns {String} date string format
 */
DateField.maskToDateStringFormat = maskToDateStringFormat;
DateField.propTypes = propTypes;
DateField.defaultProps = defaultProps;
DateField.displayName = 'DateField';
export default DateField;